import type { AccountCampaign } from '../../types/accountCampaign';
import type { Campaign } from '../../types/campaign';
import { diffCampaignVsRow, type RowContentFieldKey } from './diffCampaignVsRow';
import {
  hasEndDateDrift,
  hasRowModifications,
  hasStartDateDrift,
} from './hasRowModifications';

// P2b-3 Build View commit 6: modification-signal helpers consolidated
// for the Build View. Reuses the same helpers the role grids do; this
// module just packages the per-section breakdown so each section can
// decide independently whether to render its dot.
//
// Diff-set keys come from diffCampaignVsRow (round 1 surface). Section
// → key mapping:
//
//   Resolved name (Meta) ← metaCampaignNameTemplate, campaignDescriptor
//   Resolved label (Google) ← googleAdLabelTemplate, campaignDescriptor
//   Body copy (Meta)        ← metaAdCopy.bodyCopy
//   Headlines (Meta)        ← metaAdCopy.headlines
//   Headlines (Google)      ← googleAssets.headlines
//   Descriptions (Meta)     ← metaAdCopy.descriptions
//   Descriptions (Google)   ← googleAssets.descriptions
//   CTA                     ← metaAdCopy.ctaButton
//   Lead form               ← leadForm (covers name / formType / fields drift
//                              vs the Campaign's current default entry)
//   Path 1 / Path 2         ← googleAssets.path1 / googleAssets.path2
//   UTM                     ← utmConfig
//   Assets                  ← {meta,google}AssetsFolderUrl + otherAssetsFolderUrl
//
// rowAny + dateDrift bundle the row-level signals used by the Header
// pill and Budget & Dates section.

export type BuildViewDriftFlags = {
  // Whole-row "anything modified" — drives the header pill so buyers
  // see the customisation marker before reading every section.
  anyContent: boolean;

  // Per-section flags.
  resolvedName: boolean;
  bodyCopy: boolean;
  headlines: boolean;
  descriptions: boolean;
  cta: boolean;
  leadForm: boolean;
  path1: boolean;
  path2: boolean;
  utm: boolean;
  assets: boolean;

  // Date drift.
  startDate: boolean;
  endDate: boolean;
  dateAny: boolean;
};

export function buildDriftFlags(
  row: AccountCampaign,
  campaign: Campaign | null,
  platform: 'meta' | 'google',
): BuildViewDriftFlags {
  const empty = makeEmpty();
  // Skip the parked / transitional row types — diffCampaignVsRow
  // (and hasRowModifications) treat opt-out / unassigned as having
  // no meaningful drift to surface. Date drift still applies to
  // unassigned rows per existing helper rules.
  const startDate = hasStartDateDrift(row);
  const endDate = hasEndDateDrift(row);
  empty.startDate = startDate;
  empty.endDate = endDate;
  empty.dateAny = startDate || endDate;

  if (!campaign) return empty;
  if (row.rowType === 'opt_out' || row.rowType === 'unassigned') return empty;

  const diff = diffCampaignVsRow(row, campaign);
  const has = (k: RowContentFieldKey) => diff.has(k);

  // Resolved name dot covers both the descriptor + the platform-
  // specific template snapshot. campaignDescriptor is shared across
  // Meta + Google, so any descriptor drift surfaces the resolved-name
  // section's dot on whichever platform we're rendering.
  const resolvedName =
    has('campaignDescriptor') ||
    (platform === 'meta'
      ? has('metaCampaignNameTemplate')
      : has('googleAdLabelTemplate'));

  return {
    anyContent: hasRowModifications(row, campaign),
    resolvedName,
    bodyCopy: has('metaAdCopy.bodyCopy'),
    headlines:
      platform === 'meta' ? has('metaAdCopy.headlines') : has('googleAssets.headlines'),
    descriptions:
      platform === 'meta'
        ? has('metaAdCopy.descriptions')
        : has('googleAssets.descriptions'),
    cta: has('metaAdCopy.ctaButton'),
    // Multi Lead Form spec §1.1: formType + leadForm moved off MetaAdCopy
    // onto top-level Campaign.campaignLeadForms[]; rows snapshot a single
    // entry via row.leadForm. The diff helper merged the two old keys
    // into a single 'leadForm' key (covering name / formType / fields
    // drift in one badge).
    leadForm: has('leadForm'),
    path1: has('googleAssets.path1'),
    path2: has('googleAssets.path2'),
    utm: has('utmConfig'),
    assets:
      (platform === 'meta'
        ? has('metaAssetsFolderUrl')
        : has('googleAssetsFolderUrl')) || has('otherAssetsFolderUrl'),
    startDate,
    endDate,
    dateAny: startDate || endDate,
  };
}

function makeEmpty(): BuildViewDriftFlags {
  return {
    anyContent: false,
    resolvedName: false,
    bodyCopy: false,
    headlines: false,
    descriptions: false,
    cta: false,
    leadForm: false,
    path1: false,
    path2: false,
    utm: false,
    assets: false,
    startDate: false,
    endDate: false,
    dateAny: false,
  };
}

// Inline 8px orange dot — same visual as the role grids' modification
// signals. Optional `title` carries the mouseover detail (used by the
// date drift dot to disambiguate start vs end vs both).
export function DriftDot({
  visible,
  title,
}: {
  visible: boolean;
  title?: string;
}) {
  if (!visible) return null;
  return (
    <span
      aria-hidden="true"
      title={title ?? 'Modified from Campaign'}
      className="inline-block w-2 h-2 rounded-full bg-orange-500"
    />
  );
}

// Title disambiguator for the Budget & Dates section's combined date
// dot — shared with the role grids' Date cell.
export function dateDriftTitle(flags: BuildViewDriftFlags): string {
  if (flags.startDate && flags.endDate) return 'Both dates modified';
  if (flags.startDate) return 'Start date modified';
  if (flags.endDate) return 'End date modified';
  return '';
}
